import { useEffect, useState, RefObject } from "react";

/**
 * Tracks the vertical scroll position of the window or a specific container
 * @param containerRef - Optional ref to a scroll container element
 * @returns The current vertical scroll position
 */
const useScrollPosition = (
  containerRef?: RefObject<HTMLElement | null>,
): number => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const scrollElement = containerRef?.current || window;
    const isWindow = scrollElement === window;

    let rafId: number | null = null;
    let lastScrollY = isWindow
      ? window.scrollY
      : (scrollElement as HTMLElement).scrollTop;

    const handleScroll = () => {
      lastScrollY = isWindow
        ? window.scrollY
        : (scrollElement as HTMLElement).scrollTop;

      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          setScrollY(lastScrollY);
          rafId = null;
        });
      }
    };

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    // Set initial value
    setScrollY(lastScrollY);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef]);

  return scrollY;
};

export { useScrollPosition };
